/**
 * ⚔️ Coliseum Match Page — Phase 2.2 + Battle Visualizer.
 *
 * What this ships on top of Phase 2.1:
 *  - Sequential turn reveal: AI moves play out 1.8s apart instead of
 *    dumping log lines all at once.
 *  - Floating damage / heal numbers pop off the target hero.
 *  - Hit-flash + shake on damaged hero cards.
 *  - Caster glow on the acting hero.
 *  - Ultimate cinematic overlay (1.5s) for 100-MP moves.
 *  - SFX hooks via `window.dispatchEvent('coliseum:sfx')` — a tiny Web
 *    Audio synth plays default tones; real assets can be swapped later.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── How long each AI / human-resolved turn animation holds the screen.
//     Owner-locked Feb 2026: 1.8s "cinematic" pacing.
const ANIM_TURN_GAP_MS = 1800;
const ULT_OVERLAY_MS   = 1500;

// ─── SFX synth ─────────────────────────────────────────────────────
// Procedural audio engine — no external assets, no CDN risk, no CORS.
// Layered oscillators + a one-shot white-noise buffer give us
// chunky-feeling combat SFX. To swap in real .mp3 assets later just
// replace `_playSfx` with an Audio() factory keyed off `kind`.
let __audioCtx = null;
let __noiseBuf = null;
function _ensureAudio() {
  if (typeof window === "undefined") return null;
  if (__audioCtx) return __audioCtx;
  try {
    __audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  } catch { /* audio blocked — silent */ }
  return __audioCtx;
}
function _getNoiseBuffer() {
  const ctx = _ensureAudio();
  if (!ctx) return null;
  if (__noiseBuf) return __noiseBuf;
  // 0.4s of mono white noise
  const sampleCount = Math.floor(ctx.sampleRate * 0.4);
  const buf = ctx.createBuffer(1, sampleCount, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < sampleCount; i++) data[i] = Math.random() * 2 - 1;
  __noiseBuf = buf;
  return __noiseBuf;
}
function _tone({ freq = 440, dur = 0.18, type = "sine", gain = 0.08,
                 slideTo = null, when = 0, attack = 0.005, release = null } = {}) {
  const ctx = _ensureAudio();
  if (!ctx) return;
  try {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type; o.frequency.value = freq;
    const t0 = ctx.currentTime + when;
    const tEnd = t0 + dur;
    if (slideTo != null) {
      o.frequency.setValueAtTime(freq, t0);
      o.frequency.exponentialRampToValueAtTime(Math.max(20, slideTo), tEnd);
    }
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, tEnd);
    o.connect(g); g.connect(ctx.destination);
    o.start(t0); o.stop(tEnd + 0.02);
  } catch { /* swallow */ }
}
function _noiseBurst({ dur = 0.16, gain = 0.18, lp = 1800, when = 0 } = {}) {
  const ctx = _ensureAudio();
  const buf = _getNoiseBuffer();
  if (!ctx || !buf) return;
  try {
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const flt = ctx.createBiquadFilter();
    flt.type = "lowpass"; flt.frequency.value = lp;
    const g = ctx.createGain();
    const t0 = ctx.currentTime + when;
    const tEnd = t0 + dur;
    g.gain.setValueAtTime(gain, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, tEnd);
    src.connect(flt); flt.connect(g); g.connect(ctx.destination);
    src.start(t0); src.stop(tEnd + 0.02);
  } catch { /* swallow */ }
}
function _playSfx(detail) {
  const { kind, is_ult, crit } = detail || {};

  if (is_ult) {
    // 🌋 Ult roar — fanfare: sub thud → low brass sweep → bright bell
    _tone({ freq: 110, dur: 0.45, type: "sawtooth", gain: 0.14, slideTo: 220 });
    _tone({ freq: 220, dur: 0.50, type: "sawtooth", gain: 0.10, when: 0.05, slideTo: 440 });
    _tone({ freq: 660, dur: 0.55, type: "triangle", gain: 0.09, when: 0.18 });
    _tone({ freq: 1320, dur: 0.35, type: "sine",     gain: 0.07, when: 0.35 });
    _noiseBurst({ dur: 0.25, gain: 0.10, lp: 600, when: 0.0 });
    return;
  }

  if (kind === "heal") {
    // ✨ Heal chime — ascending bell triad with sparkle
    _tone({ freq:  880, dur: 0.22, type: "triangle", gain: 0.07 });
    _tone({ freq: 1320, dur: 0.22, type: "triangle", gain: 0.06, when: 0.07 });
    _tone({ freq: 1760, dur: 0.30, type: "triangle", gain: 0.05, when: 0.14 });
    _tone({ freq: 2640, dur: 0.18, type: "sine",     gain: 0.03, when: 0.22 });
    return;
  }

  if (crit) {
    // 💥 Crit bang — deep thump + tonal stab + bright shimmer
    _tone({ freq: 60, dur: 0.20, type: "sine", gain: 0.20, slideTo: 30 });
    _noiseBurst({ dur: 0.18, gain: 0.22, lp: 2400 });
    _tone({ freq: 180, dur: 0.16, type: "square",   gain: 0.10, when: 0.02, slideTo: 90 });
    _tone({ freq: 720, dur: 0.20, type: "triangle", gain: 0.08, when: 0.06 });
    return;
  }

  // ⚔️ Standard sword clang — noise burst + short metallic ping
  _noiseBurst({ dur: 0.10, gain: 0.16, lp: 3500 });
  _tone({ freq: 420, dur: 0.10, type: "triangle", gain: 0.07, slideTo: 240 });
  _tone({ freq: 880, dur: 0.07, type: "square",   gain: 0.04, when: 0.02 });
}
if (typeof window !== "undefined" && !window.__coliseumSfxBound) {
  window.addEventListener("coliseum:sfx", (e) => {
    if (window.__coliseumMuted) return;
    _playSfx(e.detail);
  });
  window.__coliseumSfxBound = true;
  // Restore mute preference
  try {
    window.__coliseumMuted = localStorage.getItem("coliseum_muted") === "1";
  } catch { /* swallow */ }
}
function setColiseumMuted(muted) {
  if (typeof window === "undefined") return;
  window.__coliseumMuted = !!muted;
  try { localStorage.setItem("coliseum_muted", muted ? "1" : "0"); } catch { /* swallow */ }
}
function getColiseumMuted() {
  if (typeof window === "undefined") return false;
  return !!window.__coliseumMuted;
}

const TEAM_THEME = {
  a:     { label: "TEAM A",  border: "border-red-700",   bg: "from-red-950/40 to-black/60",     text: "text-red-200" },
  b:     { label: "TEAM B",  border: "border-blue-700",  bg: "from-blue-950/40 to-black/60",    text: "text-blue-200" },
  party: { label: "PARTY",   border: "border-emerald-700", bg: "from-emerald-950/40 to-black/60", text: "text-emerald-200" },
  mobs:  { label: "ENEMIES", border: "border-purple-700", bg: "from-purple-950/40 to-black/60", text: "text-purple-200" },
};

const CLASS_DOT = {
  warrior: "bg-red-500",
  mage: "bg-purple-500",
  archer: "bg-green-500",
  ash_rogue: "bg-neutral-700",
  blue_mage: "bg-blue-500",
  cassandra_auditor: "bg-amber-400",
};

function StatBar({ label, current, max, fillClass, testid }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (current / max) * 100)) : 0;
  return (
    <div className="text-[10px]" data-testid={testid}>
      <div className="flex justify-between text-neutral-400 mb-0.5">
        <span>{label}</span>
        <span>{current} / {max}</span>
      </div>
      <div className="h-1.5 rounded-full bg-neutral-900 overflow-hidden">
        <div className={`h-full ${fillClass}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function HeroCard({ hero, isCurrentTurn, isMine, isCasting, isHitFlashing, hitFx }) {
  const dot = CLASS_DOT[hero.class_id] || "bg-neutral-500";
  const ring = isCurrentTurn ? "ring-2 ring-amber-400 shadow-[0_0_20px_-4px_rgba(251,191,36,0.6)]" : "";
  const dead = !hero.alive ? "opacity-40 grayscale" : "";
  const castingFx = isCasting ? "ring-4 ring-amber-300 shadow-[0_0_30px_-4px_rgba(251,191,36,0.95)] scale-[1.04]" : "";
  const hitClass = isHitFlashing
    ? (hitFx?.kind === "heal"
        ? "ring-2 ring-emerald-400 animate-[colHeal_0.45s_ease-in-out]"
        : "ring-2 ring-red-500 animate-[colShake_0.45s_ease-in-out]")
    : "";
  return (
    <div
      data-testid={`hero-card-${hero.hero_id}`}
      className={`relative rounded-xl border border-neutral-800 bg-black/60 p-3 min-w-[160px] transition-all duration-300 ${ring} ${dead} ${castingFx} ${hitClass}`}
    >
      {/* Floating damage/heal number */}
      {hitFx && (
        <div
          data-testid={`fx-float-${hero.hero_id}`}
          className={`pointer-events-none absolute left-1/2 -translate-x-1/2 -top-2 z-20 font-black tracking-wider animate-[colFloat_1.5s_ease-out_forwards] ${
            hitFx.kind === "heal"
              ? "text-emerald-300 text-2xl drop-shadow-[0_0_8px_rgba(52,211,153,0.85)]"
              : hitFx.crit
                ? "text-amber-200 text-3xl drop-shadow-[0_0_10px_rgba(252,211,77,1)]"
                : "text-red-300 text-2xl drop-shadow-[0_0_8px_rgba(248,113,113,0.85)]"
          }`}
        >
          {hitFx.text}
          {hitFx.crit && hitFx.kind !== "heal" && (
            <span className="block text-[10px] tracking-[0.3em] text-amber-300/90 mt-0.5">CRIT</span>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 mb-2">
        <div className="text-2xl">{hero.pet_icon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-neutral-100 truncate flex items-center gap-1">
            {hero.name}
            {isMine && <span className="text-amber-400/70 text-[9px]">(you)</span>}
            {hero.is_boss && <span className="text-red-300 text-[9px]">👑 BOSS</span>}
            {hero.is_ai_ally && <span className="text-cyan-300 text-[9px]" data-testid={`ai-ally-tag-${hero.hero_id}`}>🤖 AI</span>}
          </div>
          <div className="text-[10px] text-neutral-500 flex items-center gap-1">
            <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
            {hero.class_icon} {hero.class_name}
          </div>
        </div>
      </div>
      <div className="space-y-1.5">
        <StatBar
          label="HP"
          current={hero.hp}
          max={hero.hp_max}
          fillClass="bg-gradient-to-r from-red-700 to-red-500"
          testid={`hp-${hero.hero_id}`}
        />
        <StatBar
          label="Mana"
          current={hero.mp}
          max={hero.mp_max}
          fillClass="bg-gradient-to-r from-blue-700 to-cyan-400"
          testid={`mp-${hero.hero_id}`}
        />
      </div>
      <div className="flex flex-wrap gap-1 mt-2 text-[9px]">
        <span className="px-1.5 py-0.5 rounded-full bg-neutral-900 border border-neutral-800 text-neutral-300">
          ⚡ {hero.power_level}
        </span>
        <span className="px-1.5 py-0.5 rounded-full bg-neutral-900 border border-neutral-800 text-neutral-300">
          SPD {hero.stats?.spd ?? "?"}
        </span>
      </div>
    </div>
  );
}

function TurnOrderStrip({ heroes, turnOrder, turnIndex }) {
  const byId = Object.fromEntries(heroes.map((h) => [h.hero_id, h]));
  return (
    <div
      data-testid="turn-order-strip"
      className="rounded-xl border border-amber-900/40 bg-gradient-to-r from-amber-950/30 to-black/60 p-3 overflow-x-auto"
    >
      <div className="text-[10px] uppercase tracking-widest text-amber-300/70 mb-2">
        Speed Order · Round +∞ until someone falls
      </div>
      <div className="flex gap-2">
        {turnOrder.map((hid, idx) => {
          const h = byId[hid];
          if (!h) return null;
          const isCurrent = idx === turnIndex;
          const upNext = idx === (turnIndex + 1) % turnOrder.length;
          const dot = CLASS_DOT[h.class_id] || "bg-neutral-500";
          return (
            <div
              key={hid}
              data-testid={`turn-slot-${idx}`}
              className={`flex-shrink-0 px-2 py-1.5 rounded-lg text-[10px] flex items-center gap-1.5 border transition-all
                ${isCurrent
                  ? "bg-amber-600/40 border-amber-400 text-amber-100 font-bold scale-105"
                  : upNext
                    ? "bg-neutral-900/80 border-amber-700/40 text-amber-200/80"
                    : "bg-neutral-900/40 border-neutral-800 text-neutral-400"
                } ${!h.alive ? "opacity-40 line-through" : ""}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
              <span>{h.pet_icon}</span>
              <span className="truncate max-w-[80px]">{h.name.split(" ")[0]}</span>
              {isCurrent && <span className="text-amber-200">⚔️</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TurnTimer({ turnStartedAt, isMyTurn, timeoutSeconds }) {
  // 🩹 Oct 2026 fix — the old implementation parsed the SERVER's
  // ISO timestamp with `new Date(turnStartedAt)` and compared against
  // `Date.now()`. Any clock skew between the client and the server
  // (common on mobile) made the countdown start partway through — or
  // worse, show 0 immediately. We now anchor on the CLIENT clock when
  // the turnStartedAt value first changes, so skew is irrelevant.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!turnStartedAt) {
      setElapsed(0);
      return;
    }
    const clientAnchor = Date.now();   // mark the moment this turn arrived
    setElapsed(0);
    const id = setInterval(() => {
      setElapsed(Math.max(0, Math.floor((Date.now() - clientAnchor) / 1000)));
    }, 250);
    return () => clearInterval(id);
  }, [turnStartedAt]);
  if (!isMyTurn) return null;
  const budget = Math.max(5, Number(timeoutSeconds) || 30);
  const remaining = Math.max(0, budget - elapsed);
  const danger = remaining <= 5;
  const low = remaining <= 10;
  const pct = Math.max(0, Math.min(100, (remaining / budget) * 100));
  return (
    <div data-testid="turn-timer" className="w-full">
      <div className={`text-base font-black tabular-nums tracking-wider ${danger ? "text-red-300 animate-pulse" : low ? "text-amber-300" : "text-amber-100"}`}>
        ⏱️ {remaining}s {remaining === 0 ? "— auto-pick!" : "to act"}
      </div>
      <div className="mt-1 h-1.5 w-full rounded-full bg-neutral-800 overflow-hidden">
        <div
          className={`h-full transition-all duration-200 ${danger ? "bg-red-500" : low ? "bg-amber-400" : "bg-emerald-400"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function MovePicker({ match, hero, options, onActed }) {
  const [selectedSlot, setSelectedSlot] = useState(null); // 'basic' | 'skill' | 'ult'
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const slots = ["basic", "skill", "ult"];
  const slotMove = (s) => options?.[s] || null;

  const needsTarget = (move) => move && (move.target === "single_enemy" || move.target === "single_ally");
  const targetPool = (move) => {
    if (!move) return [];
    if (move.target === "single_enemy") {
      return match.heroes.filter((h) => h.alive && h.team !== hero.team);
    }
    if (move.target === "single_ally") {
      return match.heroes.filter((h) => h.alive && h.team === hero.team);
    }
    return [];
  };

  const submit = async (move_id, target_hero_id = null) => {
    setSubmitting(true);
    setErr(null);
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/coliseum/match/${match.match_id}/action`, {
        move_id, target_hero_id,
      });
      // 🐦‍🔥 Mar 4, 2026 — surface Phoenix Reward drop if the winning
      // move just landed. Server returns `phoenix_reward` on victory.
      const px = r?.data?.phoenix_reward?.drop;
      if (px) {
        const amt = px.amount ? ` (+${px.amount.toLocaleString()})` : "";
        toast.success(
          `🐦‍🔥 Phoenix Reward — ${px.icon || "🎁"} ${px.label || px.name || "Prize"}${amt}`,
          {
            description: px.description || "Claimed to your Phoenix Pouch.",
            duration: 7000,
          },
        );
      }
      setSelectedSlot(null);
      onActed?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Action failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const onPick = (slot) => {
    const move = slotMove(slot);
    if (!move || !move.affordable) return;
    if (needsTarget(move)) {
      setSelectedSlot(slot);
    } else {
      submit(move.id, null);
    }
  };

  return (
    <div
      data-testid="move-picker"
      className="rounded-2xl border-2 border-amber-700/60 bg-gradient-to-br from-amber-950/30 to-black/70 p-4 shadow-[0_0_30px_-10px_rgba(251,191,36,0.4)]"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300/70">Your turn</div>
          <div className="text-lg font-black text-amber-100 flex items-center gap-2" style={{ fontFamily: "Anton, sans-serif" }}>
            {hero.pet_icon} {hero.name}
          </div>
          <div className="text-[10px] text-neutral-400">{hero.class_icon} {hero.class_name} · MP {hero.mp}/{hero.mp_max}</div>
        </div>
        <TurnTimer
          turnStartedAt={match.current_turn_started_at}
          isMyTurn
          timeoutSeconds={match.current_turn_timeout_seconds}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {slots.map((slot) => {
          const m = slotMove(slot);
          if (!m) return null;
          const disabled = !m.affordable || submitting;
          const color = slot === "basic" ? "border-neutral-700 hover:border-neutral-500" :
                        slot === "skill" ? "border-blue-700/70 hover:border-blue-400" :
                                            "border-amber-600/70 hover:border-amber-400";
          const labelTag = slot === "basic" ? "BASIC" : slot === "skill" ? "SKILL" : "ULT";
          const labelColor = slot === "basic" ? "text-neutral-400" : slot === "skill" ? "text-blue-300" : "text-amber-300";
          return (
            <button
              key={slot}
              data-testid={`move-btn-${slot}`}
              onClick={() => onPick(slot)}
              disabled={disabled}
              className={`text-left rounded-xl border-2 ${color} bg-black/60 p-3 transition-all disabled:opacity-40 disabled:cursor-not-allowed ${selectedSlot === slot ? "ring-2 ring-amber-300" : ""}`}
            >
              <div className="flex items-center justify-between text-[9px] uppercase tracking-widest mb-1">
                <span className={labelColor}>{labelTag}</span>
                <span className="text-neutral-500">{m.mp_cost > 0 ? `${m.mp_cost} MP` : "FREE"}</span>
              </div>
              <div className="text-base font-bold text-neutral-100 flex items-center gap-1">
                <span className="text-xl">{m.icon}</span>{m.name}
              </div>
              <div className="text-[10px] text-neutral-400 mt-1 line-clamp-2">{m.desc}</div>
            </button>
          );
        })}
      </div>

      {selectedSlot && needsTarget(slotMove(selectedSlot)) && (
        <div className="mt-3 rounded-xl border border-amber-700/40 bg-black/60 p-3" data-testid="target-picker">
          <div className="text-[10px] uppercase tracking-widest text-amber-300/70 mb-2">
            Pick a target for {slotMove(selectedSlot).name}
          </div>
          <div className="flex flex-wrap gap-2">
            {targetPool(slotMove(selectedSlot)).map((t) => (
              <button
                key={t.hero_id}
                data-testid={`target-btn-${t.hero_id}`}
                disabled={submitting}
                onClick={() => submit(slotMove(selectedSlot).id, t.hero_id)}
                className="px-3 py-1.5 rounded-lg border border-neutral-700 hover:border-amber-400 bg-black/70 text-xs text-neutral-100 disabled:opacity-50"
              >
                {t.pet_icon} {t.name}{" "}
                <span className="text-[9px] text-neutral-500">({t.hp}/{t.hp_max})</span>
              </button>
            ))}
            <button
              onClick={() => setSelectedSlot(null)}
              data-testid="target-cancel-btn"
              className="px-3 py-1.5 rounded-lg border border-neutral-800 bg-black/70 text-xs text-neutral-400 hover:text-neutral-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {err && (
        <div className="mt-3 text-xs text-red-300" data-testid="move-error">⚠️ {err}</div>
      )}
      <p className="text-[10px] text-neutral-500 mt-3 italic">
        Idle {Math.max(5, Number(match?.current_turn_timeout_seconds) || 30)}s → auto-pick best move kicks in.
        {" "}
        <span className="not-italic text-neutral-600">
          · Need more time? Enable "Slow turns" in Settings for 90s.
        </span>
      </p>
    </div>
  );
}

function WaitingPanel({ match, currentHero }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const onForceTick = async () => {
    setBusy(true); setErr(null);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/coliseum/match/${match.match_id}/tick`);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Tick failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      data-testid="waiting-panel"
      className="rounded-2xl border border-neutral-800 bg-black/50 p-5 text-center"
    >
      <div className="text-2xl mb-2">⏳</div>
      <div className="text-sm font-bold text-neutral-200">
        {currentHero ? `${currentHero.name}'s turn` : "Resolving..."}
      </div>
      <div className="text-[10px] text-neutral-500 mt-1">
        Match polling every 3s — your turn lights up automatically.
      </div>
      <div className="text-[10px] text-neutral-600 mt-2 italic">Round {match.round}</div>
      <button
        type="button"
        onClick={onForceTick}
        disabled={busy}
        data-testid="force-tick-btn"
        className="mt-3 px-4 py-1.5 rounded-lg border border-amber-700/50 hover:border-amber-400 bg-black/60 text-xs text-amber-200 disabled:opacity-40"
      >
        {busy ? "Resolving…" : "🔄 Force Resolve (if stuck)"}
      </button>
      {err && <div className="text-[10px] text-red-300 mt-2">{err}</div>}
    </div>
  );
}

// 🪦 MatchExitRedirect — replaces the old win/lose scorecard. On match
// end, show a brief one-line toast and bounce back to the Coliseum
// lobby. Rewards hit the wallet directly, so no popup is needed.
function MatchExitRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const id = setTimeout(() => navigate("/coliseum"), 1200);
    return () => clearTimeout(id);
  }, [navigate]);
  return (
    <div
      data-testid="match-exit-redirect"
      className="rounded-2xl border border-neutral-800 bg-black/60 p-6 text-center text-neutral-400 text-sm"
    >
      Match complete — returning to the Coliseum…
    </div>
  );
}

function ForfeitButton({ matchId, matchOver }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (matchOver) return null;
  const onForfeit = async () => {
    if (!window.confirm("Forfeit this match? The opposing side wins (no rewards).")) return;
    setBusy(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/coliseum/match/${matchId}/forfeit`);
      navigate("/coliseum");
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Forfeit failed.");
    } finally {
      setBusy(false);
    }
  };
  // 🚪 QA Bug #9 (2026-10) — keep Forfeit permanently reachable. The
  // button now has a high z-index AND, on narrow screens, an extra fixed
  // escape pill in the bottom-right so a stuck player can always bail
  // even if the top bar is covered by a modal or sticky toolbar.
  return (
    <>
      <button
        type="button"
        onClick={onForfeit}
        disabled={busy}
        data-testid="forfeit-match-btn"
        title="End this match — opposing side wins, no rewards. Use this to escape a stuck match."
        className="relative z-50 px-3 py-1 rounded-full text-xs font-bold border border-red-700/60 bg-red-900/40 text-red-100 hover:bg-red-800/60 disabled:opacity-50 shadow-md shadow-red-900/30"
      >
        {busy ? "Forfeiting…" : "🏳️ Forfeit"}
      </button>
      {/* Mobile-only floating escape — guarantees the Forfeit action is
          always reachable even when the top toolbar is obscured. */}
      <button
        type="button"
        onClick={onForfeit}
        disabled={busy}
        data-testid="forfeit-match-floating-btn"
        aria-label="Forfeit match"
        title="Forfeit match — opposing side wins, no rewards"
        className="sm:hidden fixed bottom-4 right-4 z-[60] px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest border-2 border-red-500/80 bg-red-700/90 text-white shadow-lg shadow-red-900/60 disabled:opacity-50"
      >
        🏳️ Forfeit
      </button>
    </>
  );
}

function SfxMuteButton() {
  const [muted, setMutedState] = useState(getColiseumMuted());
  const toggle = () => {
    const next = !muted;
    setMutedState(next);
    setColiseumMuted(next);
    if (!next) {
      // Smoke-test ping so the user knows audio is back on
      try {
        window.dispatchEvent(new CustomEvent("coliseum:sfx", { detail: { kind: "attack", crit: false, is_ult: false } }));
      } catch { /* ignore */ }
    }
  };
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="sfx-mute-btn"
      title={muted ? "Sound off — click to unmute" : "Sound on — click to mute"}
      className={`px-3 py-1 rounded-full text-xs font-bold border ${muted ? "border-neutral-700 bg-neutral-900 text-neutral-500" : "border-amber-700/60 bg-amber-900/30 text-amber-200"}`}
    >
      {muted ? "🔇 Muted" : "🔊 SFX"}
    </button>
  );
}

export default function ColiseumMatchPage() {
  const { match_id } = useParams();
  const { isAuthed } = useAuth();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [error, setError] = useState(null);
  const [showIntro, setShowIntro] = useState(true);

  // ─── Battle visualizer state ──────────────────────────────────────
  // shownLogIdx tracks how many log entries have been animated. New
  // server log entries beyond this index get queued up and revealed
  // sequentially with a 1.8s cinematic gap.
  const [shownLogIdx, setShownLogIdx] = useState(0);
  const [activeEvent, setActiveEvent] = useState(null);  // currently animating event
  const [floatingFx, setFloatingFx] = useState({});      // { [hero_id]: { kind, text, crit, key } }
  const [ultBanner, setUltBanner] = useState(null);      // { name, icon } for the ultimate overlay
  const seenLogKeyRef = useRef(0);

  useEffect(() => {
    if (!isAuthed || !match_id) return;
    let cancelled = false;
    const fetchOnce = () => {
      axios
        .get(`${API}/coliseum/match/${match_id}`)
        .then((r) => {
          if (cancelled) return;
          setMatch(r.data);
          setError(null);
          // On the very first load, fast-forward shown-log past any
          // already-resolved entries so we don't animate ancient history.
          if (seenLogKeyRef.current === 0) {
            setShownLogIdx(r.data?.log?.length || 0);
            seenLogKeyRef.current = 1;
          }
        })
        .catch((e) => {
          if (!cancelled) setError(e?.response?.data?.detail || e?.message || "Match not found.");
        });
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isAuthed, match_id]);

  // ─── Animator: walk forward through unseen log entries one at a time
  useEffect(() => {
    if (!match) return;
    if (activeEvent) return;             // already animating one — wait
    const log = match.log || [];
    if (shownLogIdx >= log.length) return;
    const next = log[shownLogIdx];
    // Non-combat entries flash by instantly (room transitions, match
    // start banners, idle auto-pick footnotes, etc.)
    if (next.kind !== "attack" && next.kind !== "heal") {
      const tFast = setTimeout(() => setShownLogIdx((i) => i + 1), 0);
      return () => clearTimeout(tFast);
    }
    // Defer set-state to a microtask so the linter's "no synchronous
    // setState-in-effect" rule is satisfied. Animation timing is in ms,
    // so the +1 tick is imperceptible.
    let cancelled = false;
    const startTimer = setTimeout(() => {
      if (cancelled) return;
      setActiveEvent(next);
      // SFX
      try {
        window.dispatchEvent(new CustomEvent("coliseum:sfx", {
          detail: { kind: next.kind, is_ult: !!next.is_ult, crit: !!next.crit },
        }));
      } catch { /* ignore */ }
      // Ult overlay
      if (next.is_ult) {
        setUltBanner({ name: next.move_name, icon: next.move_icon, caster: next.attacker_name });
        setTimeout(() => setUltBanner(null), ULT_OVERLAY_MS);
      }
      // Floating number on the target card
      if (next.target_id) {
        const text = next.kind === "heal" ? `+${next.heal || 0}` : `-${next.damage || 0}`;
        const key = `${shownLogIdx}-${Date.now()}`;
        setFloatingFx((m) => ({ ...m, [next.target_id]: { kind: next.kind, text, crit: !!next.crit, key } }));
      }
    }, 0);
    const completeTimer = setTimeout(() => {
      if (cancelled) return;
      setActiveEvent(null);
      setShownLogIdx((i) => i + 1);
      if (next.target_id) {
        setFloatingFx((m) => {
          const nxt = { ...m }; delete nxt[next.target_id]; return nxt;
        });
      }
    }, ANIM_TURN_GAP_MS);
    return () => { cancelled = true; clearTimeout(startTimer); clearTimeout(completeTimer); };
  }, [match, shownLogIdx, activeEvent]);

  // 3-second "Match Found!" intro then fade
  useEffect(() => {
    const t = setTimeout(() => setShowIntro(false), 3000);
    return () => clearTimeout(t);
  }, []);

  // 🔊 Battle audio — loop while the match is live, fanfare on win, silence on defeat.
  useEffect(() => {
    if (!match) return;
    if (match.status === "finished") {
      const won = match.winner && match.my_team && match.winner === match.my_team;
      window.dispatchEvent(new Event(won ? "battle-audio:victory" : "battle-audio:defeat"));
    } else {
      window.dispatchEvent(new Event("battle-audio:start"));
    }
    return () => {
      // Leaving the match page silences the loop.
      window.dispatchEvent(new Event("battle-audio:stop"));
    };
  }, [match?.status, match?.winner, match?.my_team]);

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <p className="text-neutral-400">Sign in to view this match.</p>
          <Link to="/" className="inline-block mt-4 px-6 py-2 rounded bg-red-700 hover:bg-red-600 font-bold">Go Home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="match-error">
          <div className="text-5xl mb-3">😬</div>
          <p className="text-red-300 font-bold">{error}</p>
          <Link to="/coliseum" className="inline-block mt-4 px-6 py-2 rounded bg-red-700 hover:bg-red-600 font-bold">
            Back to Coliseum
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!match) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <p className="text-neutral-400 animate-pulse">Loading match…</p>
        </div>
        <Footer />
      </div>
    );
  }

  const isMPD = match.mode === "mpd";
  // Split heroes by team for layout
  const heroesByTeam = match.heroes.reduce((acc, h) => {
    (acc[h.team] = acc[h.team] || []).push(h);
    return acc;
  }, {});

  const teams = isMPD ? ["party", "mobs"] : ["a", "b"];
  const currentTurnHeroId = match.turn_order[match.turn_index];
  const currentHero = match.heroes.find((h) => h.hero_id === currentTurnHeroId);
  const isMyTurn = !!currentHero && (match.my_hero_ids || []).includes(currentTurnHeroId);
  const myActionOptions = isMyTurn ? (match.my_action_options?.[currentTurnHeroId] || null) : null;
  const matchOver = match.status === "finished";  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-black to-black text-white">
      <Header />

      {/* ⭐ Ultimate cinematic overlay */}
      {ultBanner && (
        <div
          data-testid="ult-overlay"
          className="fixed inset-0 z-40 pointer-events-none flex items-center justify-center"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/40 via-red-900/40 to-amber-900/40 animate-[colUltBg_1.5s_ease-out_forwards]" />
          <div className="relative text-center animate-[colUltText_1.5s_ease-out_forwards] px-4">
            <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300/80">{ultBanner.caster}</div>
            <div className="text-7xl my-2 drop-shadow-[0_0_20px_rgba(252,211,77,0.9)]">{ultBanner.icon}</div>
            <div
              className="text-5xl sm:text-7xl font-black text-amber-100 tracking-wider drop-shadow-[0_0_30px_rgba(252,211,77,0.9)]"
              style={{ fontFamily: "Anton, sans-serif" }}
            >
              {ultBanner.name?.toUpperCase()}!
            </div>
          </div>
        </div>
      )}

      {/* Match Found! intro overlay */}
      {showIntro && (
        <div
          data-testid="match-found-intro"
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center animate-fadeIn"
          onClick={() => setShowIntro(false)}
        >
          <div className="text-center px-6">
            <div className="text-7xl mb-4 animate-bounce">{isMPD ? "🏰" : "⚔️"}</div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300/70 mb-2">
              {isMPD ? "Dungeon Doors Slam Shut" : "Opponents Locked In"}
            </div>
            <h1
              className="text-5xl sm:text-7xl font-black text-amber-100 mb-3 drop-shadow-[0_0_30px_rgba(251,191,36,0.5)]"
              style={{ fontFamily: "Anton, sans-serif" }}
            >
              MATCH FOUND
            </h1>
            <p className="text-sm text-amber-200/80 max-w-md mx-auto">
              {isMPD
                ? "Three rooms. One boss at the deep end. Move fast."
                : "Three heroes vs three. Speed decides first strike."}
            </p>
            <p className="text-xs text-neutral-500 mt-6 italic">tap anywhere to skip</p>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="match-page">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300/70">{isMPD ? "Multi-Player Dungeon" : "3v3 Ranked"}</div>
            <h1 className="text-2xl sm:text-3xl font-black text-amber-100" style={{ fontFamily: "Anton, sans-serif" }}>
              {isMPD ? (match.party_name || "Dungeon Run") : `${match.team_a_name || "Team A"} vs ${match.team_b_name || "Team B"}`}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 rounded-full text-xs border border-amber-700/60 bg-amber-900/30 text-amber-200 font-bold">
              Round {match.round}
            </span>
            {isMPD && (
              <span className="px-3 py-1 rounded-full text-xs border border-purple-700/60 bg-purple-900/30 text-purple-200 font-bold" data-testid="mpd-room-indicator">
                Room {(match.current_room ?? 0) + 1} / {match.rooms?.length || 3}
              </span>
            )}
            <SfxMuteButton />
            <ForfeitButton matchId={match.match_id} matchOver={matchOver} />
            <Link
              to="/coliseum"
              data-testid="back-to-coliseum-btn"
              className="px-3 py-1 rounded-full text-xs bg-neutral-800 hover:bg-neutral-700 text-neutral-300 font-bold"
            >
              ← Coliseum
            </Link>
          </div>
        </div>

        {/* Turn-order strip */}
        <div className="mb-4">
          <TurnOrderStrip
            heroes={match.heroes}
            turnOrder={match.turn_order}
            turnIndex={match.turn_index}
          />
        </div>

        {/* Teams */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {teams.map((tm) => {
            const theme = TEAM_THEME[tm] || TEAM_THEME.party;
            const heroes = heroesByTeam[tm] || [];
            return (
              <div
                key={tm}
                data-testid={`team-panel-${tm}`}
                className={`rounded-2xl border-2 ${theme.border} bg-gradient-to-br ${theme.bg} p-4`}
              >
                <div className={`text-xs uppercase tracking-widest ${theme.text} font-black mb-3`}>
                  {theme.label} · {heroes.length} hero{heroes.length === 1 ? "" : "es"}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {heroes.map((h) => (
                    <HeroCard
                      key={h.hero_id}
                      hero={h}
                      isCurrentTurn={h.hero_id === currentTurnHeroId}
                      isMine={match.my_hero_ids?.includes(h.hero_id)}
                      isCasting={activeEvent?.attacker_id === h.hero_id}
                      isHitFlashing={!!floatingFx[h.hero_id]}
                      hitFx={floatingFx[h.hero_id] || null}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Action panel (Phase 2.2) + Combat log */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            {matchOver ? (
              // 🪦 Owner directive (Oct 2026): win/lose scorecard removed.
              // When a match ends we auto-return to the Coliseum lobby —
              // rewards are reflected in the wallet, no popup needed.
              <MatchExitRedirect />
            ) : isMyTurn && currentHero ? (
              <MovePicker
                match={match}
                hero={currentHero}
                options={myActionOptions}
                onActed={() => {
                  // Force an immediate refetch for snappier feedback
                  axios.get(`${API}/coliseum/match/${match_id}`).then((r) => setMatch(r.data)).catch(() => {});
                }}
              />
            ) : (
              <WaitingPanel match={match} currentHero={currentHero} />
            )}
          </div>
          <div className="rounded-2xl border border-neutral-800 bg-black/60 p-4 max-h-72 overflow-y-auto" data-testid="combat-log">
            <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2">Combat Log</div>
            <div className="space-y-1.5">
              {(match.log || []).slice(0, shownLogIdx).reverse().map((entry, i) => (
                <div key={i} className="text-xs text-neutral-300" data-testid={`combat-log-entry-${i}`}>
                  <span className="text-neutral-600">›</span> {entry.msg}
                </div>
              ))}
              {(match.log || []).slice(0, shownLogIdx).length === 0 && (
                <div className="text-xs text-neutral-500 italic">No events yet — match just started.</div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
